import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Sun, CloudSun, CloudRain, CloudLightning, Cloud, CloudFog, Snowflake, Drop, Wind, Calendar, Thermometer } from "@phosphor-icons/react";

/**
 * WeatherWidget — current conditions for Richards Bay + a 7-day forecast tab.
 * Data: GET /api/weather (Open-Meteo, cached 10 min server-side).
 */

function iconForCode(code) {
    const c = parseInt(code, 10);
    if (c === 0 || c === 1) return Sun;
    if (c === 2) return CloudSun;
    if (c === 3 || c === 45 || c === 48) return Cloud;
    if (c >= 51 && c <= 67) return CloudRain;
    if (c >= 71 && c <= 77) return Snowflake;
    if (c >= 80 && c <= 82) return CloudRain;
    if (c >= 95) return CloudLightning;
    return CloudFog;
}

function fmtDay(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

export default function WeatherWidget() {
    const [tab, setTab] = useState("current"); // current | forecast
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/weather");
                if (!cancelled) setData(data);
            } catch (e) {
                if (!cancelled) setError("Weather unavailable");
            }
        };
        load();
        const t = setInterval(load, 10 * 60 * 1000); // refresh every 10 min
        return () => { cancelled = true; clearInterval(t); };
    }, []);

    if (error) {
        return (
            <div className="g-card p-4 text-sm text-slate-500" data-testid="weather-widget">
                {error}
            </div>
        );
    }
    if (!data) {
        return (
            <div className="g-card p-4 text-sm text-slate-400" data-testid="weather-widget">
                Loading weather…
            </div>
        );
    }

    const cur = data.current || {};
    const Icon = iconForCode(cur.weather_code);

    return (
        <div className="g-card overflow-hidden shadow-sm" data-testid="weather-widget">
            <div className="bg-[var(--grindrod-navy)] text-white px-4 py-3 flex items-center gap-3 border-b-[3px] border-[var(--grindrod-red)]">
                <Icon size={20} weight="duotone" />
                <div className="flex-1 min-w-0">
                    <div className="uppercase-label text-[10px] text-slate-300">Weather</div>
                    <div className="font-display font-bold text-sm truncate" data-testid="weather-location">
                        {data.location}
                    </div>
                </div>
                <div className="flex bg-white/10 rounded-sm p-0.5 text-[11px]">
                    <button
                        onClick={() => setTab("current")}
                        className={`px-2.5 py-1 rounded-sm transition-colors ${tab === "current" ? "bg-white text-[var(--grindrod-navy)] font-semibold" : "text-white/80"}`}
                        data-testid="weather-tab-current"
                    >
                        Current
                    </button>
                    <button
                        onClick={() => setTab("forecast")}
                        className={`px-2.5 py-1 rounded-sm transition-colors ${tab === "forecast" ? "bg-white text-[var(--grindrod-navy)] font-semibold" : "text-white/80"}`}
                        data-testid="weather-tab-forecast"
                    >
                        7-day
                    </button>
                </div>
            </div>

            {tab === "current" ? (
                <div className="p-4" data-testid="weather-current">
                    <div className="flex items-center gap-4">
                        <Icon size={56} weight="duotone" className="text-[var(--grindrod-navy)] shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div
                                className="font-display font-black text-4xl text-[var(--grindrod-navy)] tabular leading-none"
                                data-testid="weather-temp"
                            >
                                {cur.temperature_c != null ? `${Math.round(cur.temperature_c)}°` : "—"}
                            </div>
                            <div
                                className="text-sm text-slate-700 mt-1 font-semibold truncate"
                                data-testid="weather-conditions"
                                title={cur.conditions}
                            >
                                {cur.conditions}
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-slate-100 text-xs">
                        <div className="flex items-center gap-1.5">
                            <Wind size={14} className="text-[var(--grindrod-navy)]" />
                            <span className="uppercase-label text-[9px]">Wind</span>
                            <span className="ml-auto tabular font-semibold">
                                {cur.wind_kmh != null ? `${Math.round(cur.wind_kmh)} km/h` : "—"}
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <Drop size={14} className="text-[var(--grindrod-navy)]" />
                            <span className="uppercase-label text-[9px]">Humidity</span>
                            <span className="ml-auto tabular font-semibold">
                                {cur.humidity != null ? `${Math.round(cur.humidity)}%` : "—"}
                            </span>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="p-2" data-testid="weather-forecast">
                    <div className="divide-y divide-slate-100">
                        {(data.forecast_7d || []).map((d, i) => {
                            const DI = iconForCode(d.weather_code);
                            return (
                                <div
                                    key={d.date}
                                    className="flex items-center gap-3 px-2 py-2"
                                    data-testid={`weather-day-${i}`}
                                >
                                    <DI size={22} weight="duotone" className="text-[var(--grindrod-navy)] shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs font-semibold text-[var(--grindrod-navy)] truncate">
                                            {i === 0 ? "Today" : fmtDay(d.date)}
                                        </div>
                                        <div className="text-[10px] text-slate-500 truncate">{d.conditions}</div>
                                    </div>
                                    {d.precip_probability != null && d.precip_probability > 0 && (
                                        <div className="flex items-center gap-1 text-[10px] text-slate-500 tabular">
                                            <Drop size={10} /> {Math.round(d.precip_probability)}%
                                        </div>
                                    )}
                                    <div className="text-xs tabular font-semibold text-[var(--grindrod-navy)] w-[58px] text-right">
                                        {d.temp_min_c != null && d.temp_max_c != null
                                            ? `${Math.round(d.temp_min_c)}° / ${Math.round(d.temp_max_c)}°`
                                            : "—"}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
